import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { ViewNavbar } from '@/administration/pages/Course/MainLayoutView/atoms/ViewNavbarV2';
import { MultiPageBody } from '@/administration/pages/Course/MainLayoutView/atoms/PageV2';
import { LayoutV2 } from '@/administration/pages/Course/MainLayoutView/atoms/LayoutV2';
import { ViewDrawerV2 } from '@/administration/pages/Course/MainLayoutView/atoms/ViewDrawerV2';
import { makeNamespacedComponent } from '@/ui/utils';
import { ViewItemV2Props } from '@/administration/pages/Course/MainLayoutView/atoms/ViewItemV2/ViewItemV2';

type ViewLayoutV2Props = {
  title: string;
  areAllActivitiesCompleted: boolean;
  content: ReactNode[];
  drawerItems: ViewItemV2Props[];
  urn: string;
  onShowHideDrawerClick: () => void;
  onAddFavoritesClick?: () => void;
  onCloseClick: () => void;
  onUpNavigationClick: () => void;
  onDownNavigationClick: () => void;
  onNewActivityClick: (activityNumber: number) => void;
  onStartPageClick?: () => void;
  isSavedToMyLearning?: boolean;
  isDrawerOpen: boolean;
  progress?: {
    prevValue: number;
    value: number;
    maxValue?: number;
  };
  activityActiveCount: number;
  activitiesTotalCount: number;
};

const ViewLayoutV2Base = ({
  title,
  content,
  drawerItems,
  urn,
  onShowHideDrawerClick,
  onAddFavoritesClick,
  onCloseClick,
  onUpNavigationClick,
  onDownNavigationClick,
  onNewActivityClick,
  isSavedToMyLearning,
  isDrawerOpen,
  progress,
  activityActiveCount,
  activitiesTotalCount,
  onStartPageClick,
  areAllActivitiesCompleted,
}: ViewLayoutV2Props): JSX.Element => {
  const activeContentContainerRef = useRef<HTMLDivElement>(null);
  const activeContent = content.find(
    (content) => (content as { props: { activity: { active: boolean } } })?.props?.activity?.active,
  );

  const computedActiveContentId = (activeContent as { props: { activity: { id: string } } })?.props?.activity?.id ?? null;

  const [activeContentId, setActiveContentId] = useState(computedActiveContentId);

  useEffect(() => {
    setActiveContentId(computedActiveContentId);
  }, [computedActiveContentId]);

  useEffect(() => {
    if (activeContentContainerRef.current) {
      activeContentContainerRef.current.scrollTo(0, 0);
    }
  }, [activeContentContainerRef.current, activityActiveCount]);

  const isNextHidden = useMemo(() => {
    return activityActiveCount === activitiesTotalCount - 1 && areAllActivitiesCompleted;
  }, [activityActiveCount, activitiesTotalCount, areAllActivitiesCompleted]);

  return (
    <LayoutV2
      navbar={
        <ViewNavbar
          title={title}
          onShowHideDrawerClick={onShowHideDrawerClick}
          onSaveFavoritesClick={onAddFavoritesClick}
          onCloseClick={onCloseClick}
          urn={urn}
          isSavedToMyLearning={isSavedToMyLearning}
          onStartPageClick={onStartPageClick}
        />
      }
      progress={progress || undefined}
      content={
        activeContent ? (
          <MultiPageBody
            ref={activeContentContainerRef}
            activePageId={computedActiveContentId}
            backDisabled={activityActiveCount === 0}
            nextHidden={isNextHidden}
            onBackClick={onUpNavigationClick}
            onNextClick={onDownNavigationClick}
            isDrawerOpen={isDrawerOpen}
          >
            {activeContent}
          </MultiPageBody>
        ) : null
      }
      drawer={<ViewDrawerV2 items={drawerItems} activeContentId={activeContentId} />}
      isDrawerOpen={isDrawerOpen}
      contentScroll={{
        page: activityActiveCount + 1,
        maxPage: activitiesTotalCount,
        onBackPage: onUpNavigationClick,
        onNextPage: onDownNavigationClick,
        onUpdatePage: onNewActivityClick,
      }}
    />
  );
};

const LoadingState = (): JSX.Element => <LayoutV2.LoadingState />;

export const ViewLayoutV2 = makeNamespacedComponent(ViewLayoutV2Base, {
  LoadingState,
});
